// ════════════════════════════════════════════════════════════════════
// SiteChrome — shared pieces of the public marketing pages (Landing and
// Our story): palette + base CSS, sticky nav with mobile drawer, footer,
// slyce-style motion (scroll reveals, pop-ins, floating tiles, mouse
// parallax, data-flow dashes) and the back-to-top button. Motion is switched
// off for visitors who ask for reduced motion.
// ════════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { setLang } from "../lib/i18n";
import qrAppStore from "../assets/qr-appstore.svg";
import qrGooglePlay from "../assets/qr-googleplay.svg";

// Netrev palette (deck navy → royal gradient, brand blue, the deck's orange).
export const NAVY = "#14226e";
export const ROYAL = "#2c50dd";
export const BLUE = "#3b5bdb";
export const INK = "#0f1b4d";
export const ORANGE = "#ff7a45";

// Calendly booking URL — the Netrev Calendly account (hello@netrev.ai).
// Set to empty string "" to hide the "Book a call" tab + embed entirely.
export const CAL_BOOKING_URL = "https://calendly.com/hello-netrev/30min";

// Live store listings for the Netrev mobile app (iOS app id 6789191491,
// Android package ai.netrev.app). Drive the footer badges + QR codes.
export const IOS_STORE_URL = "https://apps.apple.com/app/id6789191491";
export const ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=ai.netrev.app";

// Another marketing page stores a landing section here before navigating
// home; Landing scrolls to it on mount ("book" also opens the booking tab).
export const LANDING_ANCHOR_KEY = "netrev_landing_anchor";

// Marketing-page fonts only (the portal keeps Inter): Plus Jakarta Sans for
// headings and IBM Plex Sans Arabic for the Arabic page. Google serves the
// Arabic file only when Arabic text is on screen.
const FONT_LINK_ID = "lp-head-font";
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&display=swap";

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const navHeight = 72;
  const y = el.getBoundingClientRect().top + window.pageYOffset - navHeight;
  window.scrollTo({ top: y, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

export function prefersReducedMotion() {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return false;
  }
}

// "Each extra workspace: {price}{period}" → nodes. Each value is isolated so
// amounts and platform names read correctly inside Arabic sentences.
export function fill(str, vars) {
  return str.split(/(\{\w+\})/).map((part, i) => {
    const m = part.match(/^\{(\w+)\}$/);
    if (!m || vars[m[1]] == null) return part;
    return m[1] === "price" ? <bdi key={i} dir="ltr">{vars.price}</bdi> : <bdi key={i}>{vars[m[1]]}</bdi>;
  });
}

// Plus Jakarta Sans leaves a wide gap before "." and "," at heading sizes;
// tuck them in (English only — the Arabic font draws its own stops).
export function tidy(str, rtl) {
  if (rtl) return str;
  return str.split(/([.,])/).map((part, i) => (part === "." || part === "," ? <span key={i} className="lp-punct">{part}</span> : part));
}

// Loads the fonts once and reveals [data-reveal] elements as they scroll
// into view. `renderKey` re-scans after content changes (language, currency)
// so newly mounted elements are observed too.
export function useSiteMotion(rootRef, renderKey) {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll("[data-reveal]:not([data-in])"));
    const show = (el) => el.setAttribute("data-in", "");
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      els.forEach(show);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          show(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.06 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef, renderKey]);
}

// Nav shadow once the page moves, and the back-to-top button further down.
export function useScrollState() {
  const [state, setState] = useState({ scrolled: false, deep: false });
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const y = window.pageYOffset || 0;
      const scrolled = y > 8;
      const deep = y > 700;
      setState((s) => (s.scrolled === scrolled && s.deep === deep ? s : { scrolled, deep }));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return state;
}

// Mouse parallax for a hero composite: sets --mx/--my (-1…1) on the element.
export function useParallax(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return undefined;
    if (typeof window.matchMedia === "function" && !window.matchMedia("(pointer: fine)").matches) return undefined;
    let raf = 0;
    let next = null;
    const apply = () => {
      raf = 0;
      if (!next) return;
      el.style.setProperty("--mx", next[0].toFixed(3));
      el.style.setProperty("--my", next[1].toFixed(3));
    };
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      next = [Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1)), Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1))];
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      next = [0, 0];
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const zone = el.closest("section") || el;
    zone.addEventListener("mousemove", onMove);
    zone.addEventListener("mouseleave", onLeave);
    return () => {
      zone.removeEventListener("mousemove", onMove);
      zone.removeEventListener("mouseleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);
}

// ── Shared CSS (base, layout, buttons, nav, drawer, footer, motion) ──────
export const SITE_CSS = `
  html, body { overflow-x: clip !important; max-width: 100vw !important; }
  .lp { --navy:${NAVY}; --royal:${ROYAL}; --blue:${BLUE}; --ink:${INK}; --body:#4a5578; --muted:#5f6888;
    --tint:#f4f6fd; --line:#e3e8f3; --head:'Plus Jakarta Sans', 'IBM Plex Sans Arabic', Inter, -apple-system, 'Segoe UI', sans-serif;
    background:#fff; color:var(--ink); font-family:Inter, 'IBM Plex Sans Arabic', -apple-system, 'Segoe UI', sans-serif;
    overflow-x:clip; width:100%; max-width:100vw; }
  .lp.lp-rtl { --head:'IBM Plex Sans Arabic', 'Plus Jakarta Sans', Inter, -apple-system, 'Segoe UI', sans-serif;
    font-family:'IBM Plex Sans Arabic', Inter, -apple-system, 'Segoe UI', sans-serif; }
  .lp *, .lp *::before, .lp *::after { box-sizing:border-box; }
  .lp button { font-family:inherit; }
  .lp h1, .lp h2, .lp h3 { font-family:var(--head); }
  .lp-wrap { max-width:1200px; margin:0 auto; }
  .lp-sec { padding:110px 40px; }
  .lp-tint { background:var(--tint); }
  .lp-center { text-align:center; }
  .lp-h1 { font-size:60px; line-height:1.06; letter-spacing:-2px; font-weight:800; margin:0 0 22px; color:var(--ink); text-wrap:balance; }
  .lp-h2 { font-size:44px; line-height:1.1; letter-spacing:-1.4px; font-weight:800; margin:0 0 18px; color:var(--ink); text-wrap:balance; }
  .lp-rtl .lp-h1, .lp-rtl .lp-h2, .lp-rtl h3 { letter-spacing:0; line-height:1.3; }
  .lp-punct { margin-inline-start:-.13em; }
  .lp-eyebrow { font-size:15px; font-weight:700; color:var(--blue); margin-bottom:14px; }
  .lp-lead { font-size:17.5px; line-height:1.6; color:var(--body); margin:0 0 30px; max-width:560px; text-wrap:pretty; }
  .lp-lead-center { margin-left:auto; margin-right:auto; }
  .lp-nowrap { white-space:nowrap; }

  /* Hero + two-column sections (image beside copy, slyce style) */
  .lp-hero { padding:72px 40px 40px; }
  .lp-hero-grid { display:grid; grid-template-columns:1fr 1.08fr; gap:56px; align-items:center; }
  .lp-ctas { display:flex; gap:12px; flex-wrap:wrap; }
  .lp-meta { font-size:13px; color:#566080; margin:18px 0 0; font-weight:500; line-height:1.7; text-wrap:pretty; }
  .lp-split { display:grid; grid-template-columns:1fr 1.08fr; gap:72px; align-items:center; }
  .lp-flip .lp-art { order:-1; }
  .lp-copy { min-width:0; }
  .lp-art { min-width:0; }
  .lp-list { list-style:none; padding:0; margin:0 0 34px; display:grid; gap:22px; }
  .lp-list li { display:flex; gap:16px; align-items:flex-start; }
  .lp-list h3 { font-size:18px; font-weight:700; margin:5px 0 5px; letter-spacing:-.3px; color:var(--ink); }
  .lp-list p { margin:0; font-size:15px; line-height:1.55; color:var(--body); }
  .lp-check { flex-shrink:0; width:36px; height:36px; border-radius:50%; background:#fff; display:flex; align-items:center; justify-content:center;
    box-shadow:0 6px 16px -6px rgba(20,34,110,.3), 0 0 0 1px rgba(20,34,110,.06); }

  .lp-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:15px 26px; border-radius:8px; border:none;
    font-family:var(--head); font-weight:700; font-size:15px; line-height:1; letter-spacing:-.1px; cursor:pointer; text-decoration:none;
    transition:transform .15s, box-shadow .15s, background .15s; white-space:nowrap; }
  .lp-btn-primary { background:linear-gradient(180deg, ${BLUE} 0%, #2845c6 100%); color:#fff; box-shadow:0 10px 22px -10px rgba(44,80,221,.7); }
  .lp-btn-primary:hover { transform:translateY(-1px); box-shadow:0 14px 28px -12px rgba(44,80,221,.75); }
  .lp-btn-dark { background:var(--navy); color:#fff; }
  .lp-btn-dark:hover { background:#1b2c86; }
  .lp-btn-ghost { background:#fff; color:var(--navy); box-shadow:inset 0 0 0 1.5px #cbd3ec; }
  .lp-btn-ghost:hover { box-shadow:inset 0 0 0 1.5px var(--blue); }
  .lp-btn-white { background:#fff; color:var(--navy); box-shadow:0 10px 24px -12px rgba(0,0,0,.45); }
  .lp-btn-white:hover { transform:translateY(-1px); }
  .lp-btn-outline-white { background:transparent; color:#fff; box-shadow:inset 0 0 0 1.5px rgba(255,255,255,.5); }
  .lp-btn-outline-white:hover { box-shadow:inset 0 0 0 1.5px #fff; }
  .lp-btn-sm { padding:11px 18px; font-size:14px; }
  .lp-btn-full { width:100%; }
  .lp-btn:disabled { opacity:.6; cursor:wait; transform:none; }
  .lp button:focus-visible, .lp a:focus-visible, .lp input:focus-visible, .lp textarea:focus-visible { outline:3px solid rgba(59,91,219,.45); outline-offset:2px; }

  /* NAV */
  .lp-nav { position:sticky; top:0; z-index:200; background:rgba(244,246,253,.92); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
    border-bottom:1px solid rgba(20,34,110,.06); padding:0 40px; transition:box-shadow .25s, background .25s; }
  .lp-nav[data-scrolled] { background:rgba(255,255,255,.94); box-shadow:0 10px 24px -18px rgba(20,34,110,.35); }
  .lp-nav-in { height:72px; display:flex; align-items:center; justify-content:space-between; gap:20px; }
  .lp-brand { display:flex; align-items:center; gap:11px; background:none; border:none; padding:0; cursor:pointer; color:var(--ink); }
  .lp-brand img { border-radius:8px; display:block; }
  .lp-brand span { font-family:var(--head); font-size:19px; font-weight:800; letter-spacing:-.5px; }
  .lp-nav-links { display:flex; gap:30px; }
  .lp-navlink { background:none; border:none; padding:6px 0; cursor:pointer; font-size:15px; font-weight:500; color:#1f2a55; position:relative; }
  .lp-navlink::after { content:""; position:absolute; left:0; right:0; bottom:0; height:2px; border-radius:2px; background:var(--blue); transform:scaleX(0); transition:transform .25s ease; }
  .lp-navlink:hover { color:var(--blue); }
  .lp-navlink:hover::after, .lp-navlink[aria-current="page"]::after { transform:scaleX(1); }
  .lp-navlink[aria-current="page"] { color:var(--blue); }
  .lp-nav-cta { display:flex; align-items:center; gap:14px; }
  .lp-lang { padding:8px 12px; border-radius:8px; border:1px solid #d6dcee; background:#fff; color:#374151; cursor:pointer; font-size:13px; font-weight:600; }
  .lp-burger { display:none; width:40px; height:40px; align-items:center; justify-content:center; border:1px solid #d6dcee; border-radius:9px; background:#fff; cursor:pointer; padding:0; flex-shrink:0; }
  .lp-scrim { position:fixed; inset:0; background:rgba(15,23,42,.4); backdrop-filter:blur(4px); -webkit-backdrop-filter:blur(4px); z-index:299; animation:lpFade .2s ease; }
  .lp-drawer { position:fixed; top:0; bottom:0; inset-inline-end:0; width:min(85vw,320px); background:#fff; z-index:300; padding:20px 24px; box-shadow:-8px 0 32px rgba(15,23,42,.18);
    display:flex; flex-direction:column; overflow-y:auto; animation:lpSlide .25s cubic-bezier(.4,0,.2,1); }
  html[dir="rtl"] .lp-drawer { animation-name:lpSlideRtl; box-shadow:8px 0 32px rgba(15,23,42,.18); }
  .lp-drawer-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:28px; }
  .lp-drawer-link { display:block; width:100%; text-align:start; padding:13px 14px; border-radius:8px; border:none; background:none; font-size:16px; font-weight:600; color:var(--ink); cursor:pointer; }
  .lp-drawer-link:hover, .lp-drawer-link[aria-current="page"] { background:var(--tint); }
  .lp-drawer-cta { display:flex; flex-direction:column; gap:10px; padding-top:18px; margin-top:auto; border-top:1px solid #eef1f6; }
  .lp-drawer-mail { font-size:13px; color:var(--blue); font-weight:600; margin-top:18px; text-decoration:none; text-align:center; }
  @keyframes lpFade { from { opacity:0; } to { opacity:1; } }
  @keyframes lpSlide { from { transform:translateX(100%); } to { transform:translateX(0); } }
  @keyframes lpSlideRtl { from { transform:translateX(-100%); } to { transform:translateX(0); } }

  /* FOOTER */
  .lp-footer { background:linear-gradient(160deg, #101b57 0%, ${NAVY} 55%, #1a2f9a 100%); color:#cfd6f5; padding:64px 40px 30px; }
  .lp-footer-top { display:grid; grid-template-columns:1.5fr 1fr 1fr 1fr 1.3fr; gap:32px; padding-bottom:36px; border-bottom:1px solid rgba(255,255,255,.12); }
  .lp-footer-logo { display:flex; align-items:center; gap:10px; margin-bottom:14px; color:#fff; }
  .lp-footer-logo img { border-radius:8px; display:block; background:#fff; }
  .lp-footer-logo span { font-family:var(--head); font-size:19px; font-weight:800; letter-spacing:-.4px; }
  .lp-footer p { font-size:14px; line-height:1.6; margin:0 0 14px; max-width:260px; }
  .lp-footer a { color:#fff; font-weight:600; text-decoration:none; }
  .lp-footer-title { font-size:13px; font-weight:700; color:#fff; margin-bottom:14px; }
  .lp-footer-link { display:block; background:none; border:none; padding:0; margin:0 0 10px; color:#cfd6f5; font-size:14px; cursor:pointer; text-align:start; transition:color .15s, translate .2s; }
  .lp-footer-link:hover { color:#fff; translate:2px 0; }
  html[dir="rtl"] .lp-footer-link:hover { translate:-2px 0; }
  .lp-badges { display:flex; flex-direction:column; gap:10px; align-items:flex-start; }
  .lp-badge { display:inline-flex; align-items:center; gap:10px; padding:8px 14px; border-radius:10px; background:#0b1437; border:1px solid rgba(255,255,255,.2); transition:translate .2s, border-color .2s; }
  .lp-badge:hover { translate:0 -2px; border-color:rgba(255,255,255,.45); }
  .lp-badge small { display:block; font-size:9.5px; color:#a9b4cf; font-weight:500; line-height:1.2; }
  .lp-badge b { display:block; font-size:14.5px; color:#fff; font-weight:700; line-height:1.2; }
  .lp-qrs { display:flex; gap:12px; margin-top:14px; }
  .lp-qrs a { display:flex; flex-direction:column; align-items:center; gap:6px; color:#a9b4cf; font-size:10.5px; font-weight:600; }
  .lp-qrs span.lp-qr { width:72px; height:72px; background:#fff; border-radius:10px; padding:7px; display:block; }
  .lp-qrs img { display:block; width:100%; height:100%; }
  .lp-footer-bottom { display:flex; justify-content:space-between; flex-wrap:wrap; gap:12px; padding-top:22px; font-size:12.5px; color:#a9b4dc; }

  /* BACK TO TOP */
  .lp-top { position:fixed; bottom:24px; inset-inline-end:24px; z-index:150; width:48px; height:48px; border-radius:50%; border:none; cursor:pointer;
    background:var(--navy); color:#fff; display:flex; align-items:center; justify-content:center; box-shadow:0 12px 26px -10px rgba(20,34,110,.7);
    opacity:0; translate:0 16px; pointer-events:none; transition:opacity .3s, translate .3s, background .2s; }
  .lp-top[data-show] { opacity:1; translate:0 0; pointer-events:auto; }
  .lp-top:hover { background:var(--royal); }

  /* MOTION — scroll reveals (slyce: fade + 20–30px rise, 0.5s, staggered) */
  .lp [data-reveal] { opacity:0; transform:translate3d(0,26px,0);
    transition:opacity .6s ease calc(var(--d, 0) * 90ms), transform .75s cubic-bezier(.2,.65,.25,1) calc(var(--d, 0) * 90ms), translate .25s ease, box-shadow .25s ease; }
  .lp [data-reveal="left"] { transform:translate3d(-34px,0,0); }
  .lp [data-reveal="right"] { transform:translate3d(34px,0,0); }
  html[dir="rtl"] .lp [data-reveal="left"] { transform:translate3d(34px,0,0); }
  html[dir="rtl"] .lp [data-reveal="right"] { transform:translate3d(-34px,0,0); }
  .lp [data-reveal="pop"] { transform:scale(.55); transition-timing-function:ease, cubic-bezier(.2,.8,.3,1.4); }
  .lp [data-reveal][data-in] { opacity:1; transform:none; }

  /* Pop-ins inside the illustrations wait for their section to be revealed */
  .lp-pop { animation:lpPop .7s cubic-bezier(.2,.75,.25,1.15) both; animation-delay:calc(var(--d, 0) * 110ms + 200ms); }
  .lp-float { animation:lpFloat 6.5s ease-in-out infinite; animation-delay:calc(var(--d, 0) * -1.4s); }
  .lp-pop.lp-float { animation:lpPop .7s cubic-bezier(.2,.75,.25,1.15) both, lpFloat 6.5s ease-in-out infinite;
    animation-delay:calc(var(--d, 0) * 110ms + 200ms), calc(var(--d, 0) * 110ms + 1100ms); }
  .lp [data-reveal]:not([data-in]) .lp-pop { animation-play-state:paused; }
  .lp-par { translate:calc(var(--mx, 0) * var(--depth, 0) * 1px) calc(var(--my, 0) * var(--depth, 0) * 1px); transition:translate .7s cubic-bezier(.2,.7,.2,1); }
  .lp-flow { animation:lpDash 1.1s linear infinite; }
  .lp-flow-rev { animation-direction:reverse; }
  .lp-grow { transform-origin:left center; animation:lpGrow 1s cubic-bezier(.2,.7,.2,1) both; animation-delay:calc(var(--d, 0) * 120ms + 500ms); }
  .lp [data-reveal]:not([data-in]) .lp-grow { animation-play-state:paused; }
  @keyframes lpPop { from { opacity:0; scale:.82; } to { opacity:1; scale:1; } }
  @keyframes lpFloat { 0%, 100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
  @keyframes lpDash { to { stroke-dashoffset:-24; } }
  @keyframes lpGrow { from { transform:scaleX(0); } to { transform:scaleX(1); } }

  @media (prefers-reduced-motion: reduce) {
    .lp [data-reveal] { opacity:1 !important; transform:none !important; transition:none !important; }
    .lp-pop, .lp-float, .lp-flow, .lp-grow, .lp-marquee-track { animation:none !important; }
    .lp-par { translate:none !important; }
    .lp-top { transition:none; }
  }
  @media (max-width: 1100px) {
    .lp-h1 { font-size:50px; }
    .lp-h2 { font-size:38px; }
    .lp-split, .lp-hero-grid { gap:44px; }
    .lp-nav-links { gap:20px; }
    .lp-footer-top { grid-template-columns:1fr 1fr 1fr; }
  }
  @media (max-width: 1024px) {
    .lp-nav-links { display:none !important; }
    .lp-burger { display:flex; }
    .lp-nav-cta { gap:10px; }
  }
  @media (max-width: 900px) {
    .lp-desk { display:none !important; }
    .lp-nav { padding:0 16px; }
    .lp-nav-in { height:64px; }
    .lp-sec { padding:72px 20px; }
    .lp-hero { padding:28px 20px 24px; }
    .lp-hero-grid, .lp-split { grid-template-columns:1fr; gap:32px; }
    .lp-hero-copy { text-align:center; }
    .lp-hero-copy .lp-lead { margin-left:auto; margin-right:auto; }
    .lp-ctas { justify-content:center; }
    .lp-hero-art, .lp-art { order:-1; }
    .lp-h1 { font-size:38px; letter-spacing:-1.2px; }
    .lp-h2 { font-size:32px; letter-spacing:-.9px; }
    .lp-lead { font-size:16.5px; }
    .lp-footer { padding:48px 20px 26px; }
    .lp-footer-top { grid-template-columns:1fr 1fr; gap:28px 20px; }
    .lp-footer-top > :first-child, .lp-footer-top > :last-child { grid-column:1 / -1; }
    .lp-top { bottom:16px; inset-inline-end:16px; width:44px; height:44px; }
  }
  @media (max-width: 520px) {
    .lp-h1 { font-size:33px; }
    .lp-h2 { font-size:28px; }
    .lp-ctas { flex-direction:column; align-items:stretch; }
    .lp-ctas .lp-btn { width:100%; }
    .lp-nav-cta .lp-btn-sm { padding:10px 13px; font-size:13px; }
    .lp-brand span { font-size:17px; }
  }
  @media (max-width: 374px) {
    .lp-nav-in { gap:10px; }
    .lp-nav-in .lp-brand span { display:none; }
  }
`;

const STORE_BADGES = [
  {
    href: IOS_STORE_URL, aria: "landing.app.badge.iosAria", top: "landing.app.badge.iosTop", store: "App Store",
    icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.46z" /></svg>),
  },
  {
    href: ANDROID_STORE_URL, aria: "landing.app.badge.androidAria", top: "landing.app.badge.androidTop", store: "Google Play",
    icon: (<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M3.6 2.3 13.1 11.8l-2.6 2.6L3.6 2.3z" fill="#34d399" /><path d="M3.6 2.3c-.3.2-.5.6-.5 1v17.4c0 .4.2.8.5 1l8.3-8.3L3.6 2.3z" fill="#60a5fa" /><path d="m15.7 9.2 3.6 2c.7.4.7 1.4 0 1.8l-3.6 2-2.6-2.9 2.6-2.9z" fill="#fbbf24" /><path d="M13.1 11.8 3.6 21.7c.3.2.7.2 1.1 0l11-6.1-2.6-3.8z" fill="#f87171" /></svg>),
  },
];

// Sticky top nav + mobile drawer. `links` = [{key, l, fn, current?}].
export function SiteNav({ t, rtl, links, scrolled, menuOpen, setMenuOpen, onHome, onSignIn, onStartFree, onBook }) {
  const burgerRef = useRef(null);
  const closeRef = useRef(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (menuOpen) {
      wasOpen.current = true;
      if (closeRef.current) closeRef.current.focus();
      const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      if (burgerRef.current) burgerRef.current.focus({ preventScroll: true });
    }
    return undefined;
  }, [menuOpen, setMenuOpen]);

  const close = () => setMenuOpen(false);

  return (
    <>
      <nav className="lp-nav" data-scrolled={scrolled ? "" : undefined}>
        <div className="lp-wrap lp-nav-in">
          <button type="button" className="lp-brand" aria-label="Netrev.ai" onClick={onHome}>
            <img src="/logo.svg" alt="" width={32} height={32} />
            <span>Netrev.ai</span>
          </button>
          <div className="lp-nav-links">
            {links.map((n) => (
              <button type="button" key={n.key} className="lp-navlink" aria-current={n.current ? "page" : undefined} onClick={n.fn}>{n.l}</button>
            ))}
          </div>
          <div className="lp-nav-cta">
            <button type="button" className="lp-lang" onClick={() => setLang(rtl ? "en" : "ar")}>{rtl ? "EN" : "عربي"}</button>
            <button type="button" className="lp-navlink lp-desk" onClick={onSignIn}>{t("landing.nav.signIn")}</button>
            <button type="button" className="lp-btn lp-btn-dark lp-btn-sm" onClick={onStartFree}>{t("landing.nav.startFree")}</button>
            <button type="button" ref={burgerRef} className="lp-burger" aria-label={t("landing.nav.menuAria")} aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <>
          <div className="lp-scrim" onClick={close} />
          <div className="lp-drawer" role="dialog" aria-modal="true" aria-label={t("landing.nav.menuAria")}>
            <div className="lp-drawer-head">
              <span className="lp-brand"><img src="/logo.svg" alt="" width={28} height={28} /><span>Netrev.ai</span></span>
              <button type="button" ref={closeRef} className="lp-burger" style={{ display: "flex" }} aria-label={t("topbar.closeMenu")} onClick={close}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div>
              {links.map((n) => (
                <button type="button" key={n.key} className="lp-drawer-link" aria-current={n.current ? "page" : undefined} onClick={n.fn}>{n.l}</button>
              ))}
            </div>
            <div className="lp-drawer-cta">
              {CAL_BOOKING_URL && <button type="button" className="lp-btn lp-btn-ghost" onClick={onBook}>{t("landing.mobileMenu.bookDemo")}</button>}
              <button type="button" className="lp-btn lp-btn-ghost" onClick={() => { close(); onSignIn(); }}>{t("landing.nav.signIn")}</button>
              <button type="button" className="lp-btn lp-btn-primary" onClick={() => { close(); onStartFree(); }}>{t("landing.nav.startFree")}</button>
            </div>
            <a className="lp-drawer-mail" href="mailto:hello@netrev.ai">hello@netrev.ai</a>
          </div>
        </>
      )}
    </>
  );
}

// Dark footer. `cols` = [{key, title, links: [{key, l, fn}]}].
export function SiteFooter({ t, cols }) {
  return (
    <footer className="lp-footer">
      <div className="lp-wrap">
        <div className="lp-footer-top">
          <div>
            <div className="lp-footer-logo"><img src="/logo.svg" alt="" width={30} height={30} /><span>Netrev.ai</span></div>
            <p>{t("landing.footer.tagline")}</p>
            <a href="mailto:hello@netrev.ai">hello@netrev.ai</a>
          </div>
          {cols.map((col) => (
            <div key={col.key}>
              <div className="lp-footer-title">{col.title}</div>
              {col.links.map((l) => <button type="button" key={l.key} className="lp-footer-link" onClick={l.fn}>{l.l}</button>)}
            </div>
          ))}
          <div>
            <div className="lp-footer-title">{t("landing.footer.col.app")}</div>
            <div className="lp-badges">
              {STORE_BADGES.map((b) => (
                <a key={b.store} className="lp-badge" href={b.href} target="_blank" rel="noopener noreferrer" aria-label={t(b.aria)}>
                  {b.icon}
                  <span><small>{t(b.top)}</small><b>{b.store}</b></span>
                </a>
              ))}
            </div>
            <div className="lp-qrs">
              {[
                { label: t("landing.app.qr.ios"), src: qrAppStore, href: IOS_STORE_URL, aria: t("landing.app.badge.iosAria") },
                { label: t("landing.app.qr.android"), src: qrGooglePlay, href: ANDROID_STORE_URL, aria: t("landing.app.badge.androidAria") },
              ].map((q) => (
                <a key={q.href} href={q.href} target="_blank" rel="noopener noreferrer" aria-label={q.aria}>
                  <span className="lp-qr"><img src={q.src} alt="" /></span>
                  {q.label}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} Netrev.ai · {t("landing.footer.rights")}</span>
          <span>{t("landing.footer.madeIn")}</span>
        </div>
      </div>
    </footer>
  );
}

export function BackToTop({ show, label }) {
  return (
    <button type="button" className="lp-top" data-show={show ? "" : undefined} aria-label={label} tabIndex={show ? 0 : -1}
      onClick={() => window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" })}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>
    </button>
  );
}
